import React, { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { pick } from 'ramda';
import { useForm } from 'react-hook-form';

import {
  DetailsData,
  DetailsDataForSave,
  SurchargesMaintenanceDetails
} from '../../types';

import { convertDetailsForSave, handleSaveDetails } from '../../utils';

import { TableCRUDLayoutProps } from 'components/shared/CRUD/TableCRUDLayout';
import EditPage from 'components/shared/CRUD/components/InnerPage/EditPage';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import useForceRefresh from 'components/shared/hooks/useForceRefresh';

import { useEditMutation } from '../../hooks/useEditMutation';
import useGetSurchargesMaintenanceDetail from '../../hooks/useGetSurchargeDetails';
import { editDetailsSchema } from '../../schema';
import SurchargesMaintenanceFormFields from './SurchargeMaintenanceFormFields';

interface Props {
  toggleInnerLayout: () => void;
  refreshList: () => void;
  rowData: DetailsData;
  rows: TableCRUDLayoutProps['rows'];
}

const detailsProps = [
  'description',
  'method',
  'serviceBrId',
  'surcharge1Duty',
  'surcharge1Nom',
  'surcharge1Perc',
  'surcharge3Duty',
  'surcharge3Nom',
  'surcharge3Perc',
  'vatSvc',
  'surchargeDynId',
  'valid.from',
  'valid.to',
  'surchargeId'
];

const transformDetailsToEditRequest = (
  details: SurchargesMaintenanceDetails[]
) => {
  return [
    ...details.map((elem) => ({
      ...pick(detailsProps, elem),
      ...convertDetailsForSave(elem, elem.surchargeId)
    }))
  ];
};

const EditSurchargesMaintenance: React.FC<Props> = ({
  rowData,
  toggleInnerLayout,
  refreshList,
  rows
}) => {
  const { mutateAsync } = useEditMutation();
  const [currentView, setCurrentView] = useState(rowData);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);
  const { data, refetch } = useGetSurchargesMaintenanceDetail(
    currentView?.surchargeId
  );
  const { timestamp: isEditable, refresh: setIsEditable } = useForceRefresh();

  const methods = useForm<DetailsDataForSave>({
    defaultValues: editDetailsSchema.cast({
      ...currentView
    }),
    resolver: yupResolver(editDetailsSchema)
  });

  useEffect(() => {
    if (data) {
      methods.reset({
        ...editDetailsSchema.cast({
          ...currentView,
          details: data
        })
      });
    }
  }, [data, currentView]);

  const onSubmit = (data: DetailsDataForSave) => {
    mutateAsync(data).then(() => {
      handleSaveDetails(
        { ...data, details: transformDetailsToEditRequest(data.details) },
        deletedIds
      )
        .then(async () => {
          showSuccessSnackbar();
          setIsEditable();
          setDeletedIds([]);
          methods.reset(data);
          refreshList();
          await refetch();
        })
        .catch((error) => {
          showErrorSnackbar(error);
        });
    });
  };

  const handleOnCancel = () => {
    setDeletedIds([]);
    methods.reset();
  };

  return (
    <EditPage
      onCancel={handleOnCancel}
      methods={methods}
      onSubmit={methods.handleSubmit(onSubmit)}
      toggleInnerLayout={toggleInnerLayout}
      currentView={currentView}
      setCurrentView={setCurrentView}
      views={rows}
      isEditable={isEditable}
      navigatorLabelGetter={['code', 'name']}
    >
      <SurchargesMaintenanceFormFields setDeletedIds={setDeletedIds} />
    </EditPage>
  );
};

export default EditSurchargesMaintenance;
